import axiosInstance from '@api/axiosInstance';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/test-views')({
    component: RouteComponent,
});

function useCampaignProgress(campaignId) {
    const [progress, setProgress] = useState(null); // { sent, total, percent, status }
    const [error, setError] = useState(null);
    const esRef = useRef(null);

    useEffect(() => {
        if (!campaignId) return;

        const token = localStorage.getItem('access_token'); // or however you store it
        const url = `api/test-progress/${campaignId}?token=${token}`;
        // Note: EventSource doesn't support custom headers,
        // so pass the token as a query param (see auth note below)

        const es = new EventSource(url);
        esRef.current = es;

        es.addEventListener('connected', () => {
            console.log('SSE stream connected');
        });

        es.addEventListener('progress', e => {
            const data = JSON.parse(e.data);
            setProgress(data);

            if (data.status === 'complete' || data.status === 'error') {
                es.close();
            }
        });

        es.onerror = () => {
            setError('Connection lost. Reconnecting...');
            // EventSource reconnects automatically — you can clear the error on next message
        };

        return () => {
            es.close();
        };
    }, [campaignId]);

    return { progress, error };
}

const xuseSSE = id => {
    const [data, setData] = useState<T | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const connect = async () => {
            controllerRef.current = new AbortController();
            const signal = controllerRef.current.signal;

            const response = await axiosInstance.get(`test-progress/${id}`, {
                headers: {
                    Accept: 'text/event-stream',
                    'Cache-Control': 'no-cache',
                },
                signal,
                responseType: 'stream',
            });

            const decoder = new TextDecoder('utf-8');

            const reader = response.data.getReader();
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                const chunk = decoder.decode(value);
                const messages = chunk.split('\n\n').filter(Boolean);

                console.log(messages);

                for (const message of messages) {
                    if (message.startsWith('data: ')) {
                        try {
                            const eventData = JSON.parse(message.substring(6));
                            console.log(eventData);
                        } catch (e) {
                            console.error('Failed to parse SSE data:', e);
                        }
                    }
                }
            }
        };
        connect();

        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }
            setIsConnected(false);
        };
    }, []);

    return { data, error, isConnected };
};

function useSSE(id) {
    const [data, setData] = useState(null);
    const [error, setError] = useState<Error | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        const connect = async () => {
            try {
                // Create a new AbortController for this connection
                controllerRef.current = new AbortController();
                const { signal } = controllerRef.current;

                // Configure Axios for SSE
                const response = await axiosInstance({
                    method: 'GET',
                    responseType: 'stream',
                    url: `test-progress/${id}`,
                    signal,
                    headers: {
                        Accept: 'text/event-stream',
                        'Cache-Control': 'no-cache',
                    },
                    adapter: 'fetch',
                });

                // Set up a reader for the response
                const reader = response.data.getReader();
                const decoder = new TextDecoder('utf-8');

                setIsConnected(true);

                // Process the SSE stream
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    const chunk = decoder.decode(value);
                    const messages = chunk.split('\n\n').filter(Boolean);

                    for (const message of messages) {
                        if (message.startsWith('data: ')) {
                            try {
                                const eventData = JSON.parse(
                                    message.substring(6),
                                );
                                setData(eventData);
                            } catch (e) {
                                setData(message.substring(6));
                                console.error('Failed to parse SSE data:', e);
                            }
                        }
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(
                        err instanceof Error ? err : new Error(String(err)),
                    );
                    setIsConnected(false);

                    // Attempt to reconnect after a delay
                    setTimeout(connect, 5000);
                }
            }
        };

        connect();

        // Clean up the connection when the component unmounts
        return () => {
            if (controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }
            setIsConnected(false);
        };
    }, []); // Reconnect if URL changes

    return { data, error, isConnected };
}

function CampaignProgressBar({ campaignId }) {
    // const { progress, error } = useCampaignProgress(campaignId);

    // if (!progress) return <p>Waiting for campaign to start...</p>;
    const { data, error, isConnected } = useSSE('aaa');

    return (
        <div className="event-stream">
            <h2>Server Events</h2>

            <div className="status">
                Connection Status: {isConnected ? 'Connected' : 'Disconnected'}
            </div>

            {error && <div className="error">Error: {error.message}</div>}

            {data && (
                <div className="data">
                    <h3>Latest Event:</h3>
                    <pre>{JSON.stringify(data, null, 2)}</pre>
                    {data.message && <p>{data.message}</p>}
                    {data.time && (
                        <p>Time: {new Date(data.time).toLocaleTimeString()}</p>
                    )}
                    {data.value !== undefined && (
                        <p>Value: {data.value.toFixed(4)}</p>
                    )}
                </div>
            )}
        </div>
    );
}

function RouteComponent() {
    return <CampaignProgressBar campaignId="1234" />;
}
